import React from "react";
import { Box, Button, Input, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.png";
import { utils } from "@workspace/shared";
import { useStyles } from "./styles";
import { LOGIN_PAGE } from "../../util/constants";

const { validateEmail } = utils;

type RequestResetProps = {
  mode: "request";
  email: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => any;
  };
  onSubmit: (e: React.FormEvent) => void;
  errorMessage: string;
  successMessage: string;
  isLoading: boolean;
};

type ResetPasswordProps = {
  mode: "reset";
  password: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => any;
  };
  confirmPassword: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => any;
  };
  onSubmit: (e: React.FormEvent) => void;
  errorMessage: string;
  successMessage: string;
  isLoading: boolean;
};

type PropTypes = RequestResetProps | ResetPasswordProps;

const ForgotPasswordView = (props: PropTypes) => {
  const classes = useStyles();

  const isRequestMode = props.mode === "request";

  const isDisabled = () => {
    if (isRequestMode) {
      const requestProps = props as RequestResetProps;
      return !validateEmail(requestProps.email.value) || props.isLoading;
    } else {
      const resetProps = props as ResetPasswordProps;
      const password = resetProps.password.value;
      const confirmPassword = resetProps.confirmPassword.value;

      // Password validation rules
      const hasMinLength = password.length >= 8;
      const hasNumber = /\d/.test(password);
      const hasUpperCase = /[A-Z]/.test(password);
      const passwordsMatch = password === confirmPassword && password.length > 0;

      return !(hasMinLength && hasNumber && hasUpperCase && passwordsMatch) || props.isLoading;
    }
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.content}>
        <img src={logo} className={classes.logo} alt={"Logo"} />

        {isRequestMode ? (
          // Request Reset Form
          <form>
            <Typography variant={"h6"} className={classes.title} style={{ color: "white" }}>
              Reset Password
            </Typography>
            <Typography variant={"body2"} className={classes.label}>
              Enter your email address and we'll send you instructions to reset your password.
            </Typography>
            <label htmlFor={"email"} className={classes.label} style={{ marginTop: 16 }}>
              <Typography variant={"subtitle2"}>Email</Typography>
            </label>
            <Input
              id={"email"}
              className={classes.inputField}
              placeholder={"Email"}
              type={"email"}
              disableUnderline={true}
              value={(props as RequestResetProps).email.value}
              onChange={(props as RequestResetProps).email.onChange}
            />
            <div style={{ textAlign: "center" }}>
              <Button
                type={"submit"}
                variant={"contained"}
                className={classes.button}
                onClick={props.onSubmit}
                disabled={isDisabled()}
              >
                {props.isLoading ? "Sending..." : "Send"}
              </Button>
            </div>
          </form>
        ) : (
          // Reset Password Form
          <form>
            <Typography variant={"h6"} className={classes.title} style={{ color: "white" }}>
              Create New Password
            </Typography>
            <label htmlFor={"password"} className={classes.label}>
              <Typography variant={"subtitle2"}>New Password</Typography>
            </label>
            <Input
              id={"password"}
              className={classes.inputField}
              placeholder={"Password"}
              type={"password"}
              disableUnderline={true}
              value={(props as ResetPasswordProps).password.value}
              onChange={(props as ResetPasswordProps).password.onChange}
            />
            <Typography variant={"caption"} className={classes.label} style={{ fontSize: "0.7rem" }}>
              Min 8 characters, 1 uppercase, 1 number
            </Typography>
            <label htmlFor={"confirmPassword"} className={classes.label}>
              <Typography variant={"subtitle2"}>Confirm Password</Typography>
            </label>
            <Input
              id={"confirmPassword"}
              className={classes.inputField}
              placeholder={"Confirm Password"}
              type={"password"}
              disableUnderline={true}
              value={(props as ResetPasswordProps).confirmPassword.value}
              onChange={(props as ResetPasswordProps).confirmPassword.onChange}
            />
            <div style={{ textAlign: "center" }}>
              <Button
                type={"submit"}
                variant={"contained"}
                className={classes.button}
                onClick={props.onSubmit}
                disabled={isDisabled()}
              >
                {props.isLoading ? "Resetting..." : "Reset"}
              </Button>
            </div>
          </form>
        )}

        {props.errorMessage && (
          <Typography
            className={classes.errorMessage}
            color={"error"}
            variant={"body2"}
          >
            {props.errorMessage}
          </Typography>
        )}

        {props.successMessage && (
          <Typography
            className={classes.successMessage}
            variant={"body2"}
          >
            {props.successMessage}
          </Typography>
        )}

        <Link to={LOGIN_PAGE} className={classes.backLink}>
          <Typography variant={"body2"}>Back to Login</Typography>
        </Link>
      </Box>
    </Box>
  );
};

export default ForgotPasswordView;
